import * as sortBy from 'lodash/sortBy'
import * as isNumber from 'lodash/isNumber'
import { State } from 'interfaces/state'
import { Omit } from 'interfaces'
import {
  LinkageDataState,
  LinkageForm,
  LinkageProgress,
  LinkageSession,
  LinkedMixedAccount,
  LiveUpdateInfo,
  ManualBalanceAccount,
  ManualBalanceAccountCollection,
  MixedCompositeId,
  MixedLinkageEntity,
  MixedLinkageEntityType,
  OperationConfirmation,
  Provider,
  ProviderAccount,
  ProviderAccountCollection,
  ProviderAccountState,
  ProviderBalanceAccount,
  WithLinkageSessionId,
} from 'interfaces/linkage'
import { createSelector, ParametricSelector } from 'reselect'
import { getItemFromCollection, getItemsByKey, getItemsFromCollection, concatCollections } from 'helpers/normalizeHelpers'
import { extractLinkageEntityFromCid, getProviderInfo } from 'helpers/linkageHelpers'
import { Collection } from 'interfaces'
import { dateNowSelector, metaSelector } from 'selectors/globalSelectors'
import { AccountCategory, AccountCategoryId, AssetCategory, Category, DebtAssetCategory, DebtCategory } from 'interfaces/meta'
import { Option } from 'interfaces/component'
import { isAccountCategory, isMixedAccountInProgress } from 'helpers/accountHelpers'
import groupBy from 'lodash/groupBy'

export const providersSelector = (state: State): LinkageDataState['providers'] =>
  state.data.linkage.providers

export const providerAccountsSelector = (state: State): LinkageDataState['providerAccounts'] =>
  state.data.linkage.providerAccounts

export const providerAccountStatesSelector = (state: State): LinkageDataState['providerAccountStates'] =>
  state.data.linkage.providerAccountStates

export const providerBalanceAccountsSelector = (state: State): LinkageDataState['providerBalanceAccounts'] =>
  state.data.linkage.providerBalanceAccounts

export const manualBalanceAccountsSelector = (state: State): LinkageDataState['manualBalanceAccounts'] =>
  state.data.linkage.manualBalanceAccounts

export const recentlyAddedAccountsSelector = (state: State): LinkageDataState['recentlyAddedAccounts'] =>
  state.data.linkage.recentlyAddedAccounts


const accountsCategories = createSelector(
  metaSelector,
  (meta) => concatCollections<Category, 'id'>(meta.asset_categories, meta.debt_categories)
)

export const providerAccountsTableSelector = createSelector(
  providersSelector,
  providerAccountsSelector,
  providerAccountStatesSelector,
  providerBalanceAccountsSelector,
  (providers, providerAccounts, providerAccountStates, providerBalanceAccounts) => {
    if (!providerAccounts || !providerBalanceAccounts || !providers || !providerAccountStates) {
      return []
    }

    const accountsByProviderAccountMap = groupBy(getItemsFromCollection<ProviderBalanceAccount, 'cid'>(providerBalanceAccounts), 'providerAccountCid')

    return providerAccounts.allKeys.reduce((acc, providerAccountCid) => {
      const providerAccount = providerAccounts.byKey[providerAccountCid]
      const provider = providers.byKey[providerAccount.providerCid]
      const providerAccountState = providerAccountStates.byKey[providerAccountCid]

      const accounts: (ProviderBalanceAccount | null)[] = accountsByProviderAccountMap[providerAccountCid] || []
      if (accounts.length === 0) {
        accounts.push(null)
      }

      const flatAccounts = accounts.map((account) => ({
        type: 'provider' as const,
        account,
        provider,
        providerAccount,
        providerAccountState,
      }))

      return [
        ...acc,
        ...flatAccounts,
      ]
    }, [])
  }
)

const MANUAL_ACCOUNT_STATE: Omit<ProviderAccountState, 'cid'> = {
  actions: [
    { id: 'delete', title: 'Delete' },
  ],
  extra_message: '',
  message: '',
  notification_type: 'success',
  status: 'ready',
}

export const manualAccountsTableSelector = createSelector(
  manualBalanceAccountsSelector,
  (manualBalanceAccounts) => {
    if (!manualBalanceAccounts) {
      return []
    }

    return manualBalanceAccounts.allKeys.map((key) => {
      const account = manualBalanceAccounts.byKey[key]

      return {
        type: 'manual' as const,
        account,
        provider: null,
        providerAccount: null,
        providerAccountState: { ...MANUAL_ACCOUNT_STATE },
      }
    })
  }
)

export const mixedAccountsTableSelector = createSelector(
  providerAccountsTableSelector,
  manualAccountsTableSelector,
  accountsCategories,
  recentlyAddedAccountsSelector,
  (providerAccounts, manualAccounts, categories, recentlyAddedAccounts) => {
    return [
      ...providerAccounts,
      ...manualAccounts,
    ].map((flatAccount) => {
      const providerInfo = getProviderInfo(flatAccount)
      const { providerKey: providerGroupByKey } = providerInfo

      const { account, providerAccount, provider, type, providerAccountState } = flatAccount
      const isManual = type === 'manual'
      const {
        category = null,
        name = null,
        amount = null,
        cid = null,
        link_status = null,
      } = account || {}

      const { status } = providerAccountState

      const {
        name: providerName = isManual ? 'Manual Entry' : '',
      } = provider || {}

      const key = cid
        ? cid
        : providerAccount
          ? providerAccount.cid
          : null

      const {
        created_at = null,
      } = providerAccount || {}
      const createdAt = created_at
        ? +new Date(created_at)
        : null

      const isReady = status === 'ready' || type === 'manual'
      const isPinned = providerAccount ? recentlyAddedAccounts[providerAccount.cid] || false : false
      const isLinked = !!link_status && link_status === 'active'
      const isUnlinked = link_status === 'unlinked'

      const pinnedRate = isPinned ? 0 : 1
      // const unlinkedRate = isLinked && isReady ? 1 : 0
      const unlinkedRate = isReady && isLinked
        ? 0
        : isUnlinked
          ? 2
          : 1
      const nameRate = name ? 0 : 1

      const summableAmount = isLinked && isReady ? amount || 0 : 0

      const categoryName = category
        ? categories.byKey[category].value.toLocaleLowerCase()
        : null
      const categoryRate = !!categoryName ? 0 : 1


      return {
        ...flatAccount,
        providerInfo,

        // debug only
        // status,
        // link_status,
        // debug only

        // used in LinkedAccounts component
        providerGroupByKey,

        pinnedRate,
        createdAt: isPinned ? createdAt : 0,

        nameRate,
        providerName: providerName.toLocaleLowerCase(),
        name: name ? name.toLocaleLowerCase() : name,
        amount,
        summableAmount,
        unlinkedRate,

        categoryRate,
        categoryName,

        key,
      }
    })
  }
)

export type MixedAccountsTableItem = ReturnType<typeof mixedAccountsTableSelector>[0]


export const popularProvidersSelector = createSelector(
  providersSelector,
  (state: State) => state.data.linkage.popularProviderCids,
  (providers, popularProviderIds) => {
    if (!providers || !popularProviderIds) {
      return null
    }

    return getItemsByKey(providers.byKey, popularProviderIds)
  },
)

export const foundProvidersSelector = createSelector(
  providersSelector,
  (state: State) => state.data.linkage.foundProviderCids,
  (providers, foundProviderIds): Provider[] | null => {
    if (!providers || !foundProviderIds) {
      return null
    }

    return getItemsByKey(providers.byKey, foundProviderIds)
  },
)

export const providersSearchQuerySelector = (state: State) =>
  state.data.linkage.searchQuery

export const getSortedMixedAccounts = (providerAccounts: ProviderAccountCollection | null, manualBalanceAccounts: ManualBalanceAccountCollection | null): LinkedMixedAccount[] | null => {
  const mixedAccounts: (ProviderAccount | ManualBalanceAccount)[] = []
  if (providerAccounts) {
    mixedAccounts.push(...getItemsFromCollection<ProviderAccount, 'cid'>(providerAccounts))
  }
  if (manualBalanceAccounts) {
    mixedAccounts.push(...getItemsFromCollection<ManualBalanceAccount, 'cid'>(manualBalanceAccounts))
  }

  if (!mixedAccounts.length) {
    return null
  }

  return sortBy(mixedAccounts, ({ created_at }) => created_at)
}

export const sortedLinkedMixedAccountsSelector = createSelector(
  providerAccountsSelector,
  manualBalanceAccountsSelector,
  getSortedMixedAccounts,
)

export const isAnyMixedAccountInProgressSelector = createSelector(
  sortedLinkedMixedAccountsSelector,
  providerAccountStatesSelector,
  (mixedAccounts, accountStates): boolean | null => {
    if (!mixedAccounts) {
      return null
    }

    return mixedAccounts.some(isMixedAccountInProgress(accountStates))
  },
)

export const linkedMixedAccountCidsSelector = createSelector(
  sortedLinkedMixedAccountsSelector,
  (sortedMixedAccounts): MixedCompositeId[] | null =>
    sortedMixedAccounts ? sortedMixedAccounts.map(({ cid }) => cid) : null,
)

// TODO: test
const createMixedLinkageEntitySelector = (selectorEntityType: MixedLinkageEntityType | null) => <T extends MixedLinkageEntity>(state: State, ownProps: { cid: MixedCompositeId | null}): T | null => {
  if (!ownProps.cid) {
    return null
  }

  const entityFromCid = extractLinkageEntityFromCid(ownProps.cid)

  if (!entityFromCid || (selectorEntityType && entityFromCid.type !== selectorEntityType)) {
    return null
  }

  let source: null | Collection<T, 'cid'> = null
  switch (entityFromCid.type) {
    case 'provider':
      source = state.data.linkage.providers as Collection<T, 'cid'>
      break

    case 'provider-account':
      source = state.data.linkage.providerAccounts as Collection<T, 'cid'>
      break

    case 'provider-balance-account':
      source = state.data.linkage.providerBalanceAccounts as Collection<T, 'cid'>
      break

    case 'manual-balance-account':
      source = state.data.linkage.manualBalanceAccounts as Collection<T, 'cid'>
      break

    default:
      return null
  }

  return getItemFromCollection(source, ownProps.cid)
}

export const createProviderSelector = (): ParametricSelector<State, { cid: MixedCompositeId | null }, Provider | null> => createMixedLinkageEntitySelector('provider')
export const createProviderAccountSelector = (): ParametricSelector<State, { cid: MixedCompositeId | null }, ProviderAccount | null> => createMixedLinkageEntitySelector('provider-account')
export const createManualBalanceAccountSelector = (): ParametricSelector<State, { cid: MixedCompositeId | null }, ManualBalanceAccount | null> => createMixedLinkageEntitySelector('manual-balance-account')
export const createProviderAccountStateSelector = () => (state: State, ownProps: { cid: MixedCompositeId | null }): ProviderAccountState | null =>
  getItemFromCollection(state.data.linkage.providerAccountStates, ownProps.cid)

export const providerAccountSelector = createProviderAccountSelector()

export const anyMixedLinkageEntitySelector: ParametricSelector<State, { cid: MixedCompositeId | null }, ProviderAccount | null> =
  createMixedLinkageEntitySelector(null)

export const createProviderByCidSelector = () => createSelector(
  createProviderAccountSelector(),
  providersSelector,
  (providerAccount, providers): Provider | null => {
    if (!providers || !providerAccount) {
      return null
    }

    const { providerCid } = providerAccount
    return providers.byKey[providerCid]
  },
)

export const createProviderBalanceAccountsByCidSelector = () => createSelector(
  createProviderAccountSelector(),
  providerBalanceAccountsSelector,
  (providerAccount, providerBalanceAccounts): ProviderBalanceAccount[] | null => {
    if (!providerAccount || !providerBalanceAccounts) {
      return null
    }

    const { providerBalanceAccountCids } = providerAccount
    if (!providerBalanceAccountCids) {
      return null
    }

    return providerBalanceAccountCids.map((cid) => providerBalanceAccounts.byKey[cid])
  },
)

export const linkageSessionSelector = (state: State): LinkageSession | null => {
  const { sessions } = state.app.linkage
  return sessions.allKeys.length
    ? sessions.byKey[sessions.allKeys[0]]
    : null
}

export const createLinkageSessionByIdSelector = () => createSelector(
  linkageSessionByIdSelector,
  (session) => session
)

const linkageSessionByIdSelector = (state: State, ownProps: WithLinkageSessionId<true>): LinkageSession | null => {
  if (!ownProps.sessionId) {
    return null
  }

  const { sessions } = state.app.linkage
  return sessions.allKeys.length
    ? sessions.byKey[ownProps.sessionId]
    : null
}

export const providerAccountBySessionSelector = createSelector(
  linkageSessionSelector,
  providerAccountsSelector,
  (session, providerAccounts): ProviderAccount | null => {
    if (!session || !providerAccounts) {
      return null
    }

    const { cid } = session
    return providerAccounts.byKey[cid] || null
  },
)

export const providerAccountStateBySessionSelector = createSelector(
  linkageSessionSelector,
  providerAccountStatesSelector,
  (session, providerAccountStates): ProviderAccountState | null => {
    if (!session || !providerAccountStates) {
      return null
    }

    const { cid } = session
    return providerAccountStates.byKey[cid] || null
  },
)

export const providerBySessionSelector = createSelector(
  linkageSessionSelector,
  providersSelector,
  providerAccountsSelector,
  (session, providers, providerAccounts): Provider | null => {
    if (!session || !providers) {
      return null
    }

    const { cid } = session
    const entityFromCid = extractLinkageEntityFromCid(cid)
    if (!entityFromCid) {
      return null
    }

    if (entityFromCid.type === 'provider') {
      return providers.byKey[cid] || null
    } else if (entityFromCid.type === 'provider-account') {
      if (!providerAccounts) {
        return null
      }

      const providerAccount = providerAccounts.byKey[cid]
      if (!providerAccount) {
        return null
      }

      return providers.byKey[providerAccount.providerCid] || null
    }

    return null
  },
)

export const mixedAccountConfirmationSelector = (state: State): OperationConfirmation | null => {
  const { confirmations } = state.app.linkage
  if (!confirmations || confirmations.allKeys.length === 0) {
    return null
  }

  return confirmations.byKey[confirmations.allKeys[0]]
}

export const linkageFormSelector = (state: State, ownProps: { cid: MixedCompositeId }): LinkageForm | null =>
  getItemFromCollection(state.data.linkage.linkageForms, ownProps.cid)

export const linkageFormBySessionSelector = createSelector(
  linkageSessionSelector,
  (state: State) => state.data.linkage.linkageForms,
  (session, forms): LinkageForm | null => {
    if (!session) {
      return null
    }

    return getItemFromCollection(forms, session.cid)
  },
)

export const linkageFormTimeoutBySessionSelector = createSelector(
  linkageFormBySessionSelector,
  dateNowSelector,
  (form, now): number | null => {
    if (!form || !isNumber(form.expiresAt)) {
      return null
    }

    return form.expiresAt - now
  },
)

export const liveUpdateSelector = (state: State): LiveUpdateInfo | null =>
  state.data.linkage.liveUpdate

export const isLiveUpdateStartedSelector = createSelector(
  liveUpdateSelector,
  (liveUpdate) => Boolean(liveUpdate && liveUpdate.status === 'started'),
)

export const manualAccountsCategoryOptionsSelector = createSelector(
  metaSelector,
  (meta): Option<AccountCategoryId>[] => {
    const debtCategories = meta.debt_categories as Collection<DebtCategory, 'id'>
    const assetCategories = meta.asset_categories as Collection<AssetCategory, 'id'>
    const allCategoriesArray: DebtAssetCategory[] = [...getItemsFromCollection(debtCategories), ...getItemsFromCollection(assetCategories)]

    return allCategoriesArray
      .filter((category) => category.id as string !== 'all')
      .filter<AccountCategory>(isAccountCategory)
      .map((category) => ({
        value: category.id,
        label: category.value,
      }))
  },
)

export const createLinkageProgressSelector = () => (state: State, ownProps: { cid: MixedCompositeId }): LinkageProgress | null =>
  state.app.linkage.progress.byKey[ownProps.cid] || null

export const createIsLinkageInProgressSelector = () => createSelector(
  createLinkageProgressSelector(),
  (linkage) => linkage && linkage.state !== 'stopped'
)
